import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtRole } from './UI';

// EventModal handles three modes:
//   create   -- blank form, POST on save
//   edit     -- pre-filled, PUT on save (creator/admin only)
//   view     -- read-only with Accept/Decline if invitee, Cancel if creator
// Mode is auto-derived: no event passed -> create; passed event + caller is
// creator/admin -> editable; otherwise view.
//
// Props:
//   event       -- existing event row (with .attendees) or null for create
//   currentUser -- { id, role, full_name }
//   onClose     -- () => void
//   onSaved     -- (saved) => void  (called after create / edit / cancel /
//                  respond so the parent can refresh)

const TYPES = [
  'Luncheon', 'Dinner', 'Meeting', 'Lunch & Learn', 'Golf',
  'Presentation', 'Trade Show', 'Network Meeting', 'Job Walk', 'Other'
];

const TYPE_EMOJI = {
  'Luncheon': '🍽️', 'Dinner': '🍷', 'Meeting': '🤝', 'Lunch & Learn': '🎓',
  'Golf': '⛳', 'Presentation': '🎤', 'Trade Show': '🏪',
  'Network Meeting': '🌐', 'Job Walk': '👷', 'Other': '📅'
};

const today = () => new Date().toISOString().slice(0, 10);

const blank = () => ({
  title: '',
  event_type: 'Meeting',
  event_date: today(),
  start_time: '',
  end_time: '',
  location: '',
  customer: '',
  notes: '',
  attendee_user_ids: []
});

export default function EventModal({ event, currentUser, onClose, onSaved }) {
  const isCreate = !event;
  const isCreator = !!event && event.created_by === currentUser?.id;
  const canEdit = isCreate || isCreator || ['admin', 'leadership'].includes(currentUser?.role);

  const [form, setForm] = useState(() => {
    if (isCreate) return blank();
    return {
      title: event.title || '',
      event_type: event.event_type || 'Meeting',
      event_date: String(event.event_date || today()).slice(0, 10),
      start_time: (event.start_time || '').slice(0, 5),
      end_time: (event.end_time || '').slice(0, 5),
      location: event.location || '',
      customer: event.customer || '',
      notes: event.notes || '',
      attendee_user_ids: (event.attendees || []).map(a => a.user_id)
    };
  });
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.listActiveUsers().then(setUsers).catch(() => {});
  }, []);

  // My current response if I'm an invitee.
  const myAttendance = useMemo(() => {
    if (!event || !currentUser) return null;
    return (event.attendees || []).find(a => a.user_id === currentUser.id) || null;
  }, [event, currentUser]);

  const toggleAttendee = (uid) => {
    setForm(f => ({
      ...f,
      attendee_user_ids: f.attendee_user_ids.includes(uid)
        ? f.attendee_user_ids.filter(x => x !== uid)
        : [...f.attendee_user_ids, uid]
    }));
  };

  const save = async () => {
    setError(''); setSaving(true);
    try {
      const body = { ...form };
      // Strip empty time strings so the server stores NULL.
      if (!body.start_time) body.start_time = null;
      if (!body.end_time) body.end_time = null;
      let saved;
      if (isCreate) {
        saved = await api.createEvent(body);
      } else {
        // PUT doesn't accept attendee_user_ids -- use invite/remove endpoints
        // for that. v1 only edits the event fields here.
        const { attendee_user_ids, ...fields } = body;
        saved = await api.updateEvent(event.id, fields);
      }
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const respond = async (response) => {
    setError(''); setSaving(true);
    try {
      const saved = await api.respondToEvent(event.id, response);
      onSaved?.(saved);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const cancel = async () => {
    if (!confirm('Cancel this event? It will be marked Cancelled (not deleted).')) return;
    setError(''); setSaving(true);
    try {
      const saved = await api.cancelEvent(event.id);
      onSaved?.(saved);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>
            {TYPE_EMOJI[form.event_type] || '📅'}{' '}
            {isCreate ? 'Schedule Event' : (canEdit ? 'Edit Event' : 'Event')}
          </h2>
          <button style={closeBtn} onClick={onClose}>✕</button>
        </div>

        {error && <div style={{ ...styles.errorBox, marginBottom: 12 }}>{error}</div>}

        {/* When viewing as invitee, show creator info first */}
        {!isCreate && !canEdit && event.created_by_name && (
          <div style={{ background: '#F3F4F6', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            Invited by <strong>{event.created_by_name}</strong>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Type">
            <select
              style={styles.input}
              value={form.event_type}
              disabled={!canEdit || saving}
              onChange={e => setForm({ ...form, event_type: e.target.value })}
            >
              {TYPES.map(t => <option key={t} value={t}>{TYPE_EMOJI[t]} {t}</option>)}
            </select>
          </Field>
          <Field label="Date">
            <input
              type="date"
              style={styles.input}
              value={form.event_date}
              disabled={!canEdit || saving}
              onChange={e => setForm({ ...form, event_date: e.target.value })}
            />
          </Field>
          <Field label="Start time">
            <input
              type="time"
              style={styles.input}
              value={form.start_time}
              disabled={!canEdit || saving}
              onChange={e => setForm({ ...form, start_time: e.target.value })}
            />
          </Field>
          <Field label="End time">
            <input
              type="time"
              style={styles.input}
              value={form.end_time}
              disabled={!canEdit || saving}
              onChange={e => setForm({ ...form, end_time: e.target.value })}
            />
          </Field>
        </div>

        <Field label="Title (optional — defaults to type + customer)">
          <input
            style={styles.input}
            placeholder={form.customer ? `${form.event_type} · ${form.customer}` : `${form.event_type} on ${form.event_date}`}
            value={form.title}
            disabled={!canEdit || saving}
            onChange={e => setForm({ ...form, title: e.target.value })}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Location">
            <input
              style={styles.input}
              placeholder="Restaurant, customer site, golf course…"
              value={form.location}
              disabled={!canEdit || saving}
              onChange={e => setForm({ ...form, location: e.target.value })}
            />
          </Field>
          <Field label="Customer">
            <input
              style={styles.input}
              placeholder="Account / company name"
              value={form.customer}
              disabled={!canEdit || saving}
              onChange={e => setForm({ ...form, customer: e.target.value })}
            />
          </Field>
        </div>

        <Field label="Notes / agenda">
          <textarea
            style={{ ...styles.input, minHeight: 70, resize: 'vertical' }}
            placeholder="What you're hoping to accomplish, who's attending, etc."
            value={form.notes}
            disabled={!canEdit || saving}
            onChange={e => setForm({ ...form, notes: e.target.value })}
          />
        </Field>

        {/* Attendee picker -- only on create. Edit mode shows current
            attendees as chips with remove buttons (not yet implemented;
            v2). Read-only mode shows the attendee list with their
            response. */}
        {isCreate ? (
          <Field label={`Invite for support (${form.attendee_user_ids.length} selected)`}>
            <div style={attendeeList}>
              {users.map(u => (
                <label key={u.id} style={attendeeRow}>
                  <input
                    type="checkbox"
                    checked={form.attendee_user_ids.includes(u.id)}
                    onChange={() => toggleAttendee(u.id)}
                  />
                  <span style={{ flex: 1 }}>{u.full_name}</span>
                  <span style={roleChip(u.role)}>{fmtRole(u.role)}</span>
                </label>
              ))}
              {!users.length && <div style={{ color: colors.textMuted, fontSize: 12 }}>Loading users…</div>}
            </div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6 }}>
              Each invitee gets an email and a Pending entry on their dashboard.
              The event won't show on their calendar until they Accept.
            </div>
          </Field>
        ) : (
          (event.attendees || []).length > 0 && (
            <Field label={`Attendees (${(event.attendees || []).length})`}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(event.attendees || []).map(a => (
                  <span key={a.id} style={attendeeBadge(a.response)}>
                    {a.full_name} <span style={{ opacity: 0.7, marginLeft: 4 }}>· {a.response}</span>
                  </span>
                ))}
              </div>
            </Field>
          )
        )}

        {/* Action footer. Three configurations:
            - create: Save / Cancel
            - canEdit (creator/admin): Save / Cancel Event / Close
            - invitee view-only with Pending: Accept / Decline / Close
            - invitee view-only with response set: response chip + Close */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20, paddingTop: 16, borderTop: `1px solid ${colors.border}` }}>
          {isCreate && (
            <>
              <button style={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
              <button style={styles.btn} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : '+ Schedule Event'}
              </button>
            </>
          )}
          {!isCreate && canEdit && (
            <>
              <button style={{ ...styles.btnSecondary, color: colors.red }} onClick={cancel} disabled={saving}>
                Cancel event
              </button>
              <button style={styles.btnSecondary} onClick={onClose} disabled={saving}>Close</button>
              <button style={styles.btn} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </>
          )}
          {!isCreate && !canEdit && myAttendance && (
            <>
              {myAttendance.response === 'Pending' ? (
                <>
                  <button style={{ ...styles.btnSecondary, color: colors.red }} onClick={() => respond('Declined')} disabled={saving}>
                    Decline
                  </button>
                  <button style={styles.btn} onClick={() => respond('Accepted')} disabled={saving}>
                    {saving ? 'Saving…' : 'Accept'}
                  </button>
                </>
              ) : (
                <>
                  <span style={{ alignSelf: 'center', fontSize: 13, color: colors.textMuted, marginRight: 8 }}>
                    You {myAttendance.response.toLowerCase()} this invitation
                    {myAttendance.response === 'Accepted' && (
                      <button
                        style={{ ...styles.btnSecondary, marginLeft: 8, fontSize: 12, padding: '4px 8px' }}
                        onClick={() => respond('Declined')}
                        disabled={saving}
                      >Change to Decline</button>
                    )}
                    {myAttendance.response === 'Declined' && (
                      <button
                        style={{ ...styles.btnSecondary, marginLeft: 8, fontSize: 12, padding: '4px 8px' }}
                        onClick={() => respond('Accepted')}
                        disabled={saving}
                      >Change to Accept</button>
                    )}
                  </span>
                  <button style={styles.btn} onClick={onClose}>Close</button>
                </>
              )}
            </>
          )}
          {!isCreate && !canEdit && !myAttendance && (
            <button style={styles.btn} onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Local styles -----------------------------------------------------

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 4 }}>
      {label}
    </label>
    {children}
  </div>
);

const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '40px 16px', zIndex: 1000, overflowY: 'auto'
};

const modalCard = {
  background: '#fff', borderRadius: 8, padding: 24,
  maxWidth: 640, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
};

const closeBtn = {
  background: 'none', border: 'none', fontSize: 22, cursor: 'pointer',
  color: colors.textMuted, padding: 4, lineHeight: 1
};

const attendeeList = {
  border: `1px solid ${colors.border}`, borderRadius: 6,
  maxHeight: 220, overflowY: 'auto', padding: 8
};

const attendeeRow = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 13
};

const roleChip = (role) => {
  const palette = {
    admin:      { bg: '#FEE2E2', fg: '#991B1B' },
    leadership: { bg: '#DBEAFE', fg: '#1E40AF' },
    supervisor: { bg: '#FEF3C7', fg: '#92400E' },
    rep:        { bg: '#D1FAE5', fg: '#065F46' },
    ops:        { bg: '#E5E7EB', fg: '#374151' }
  };
  const p = palette[role] || palette.ops;
  return {
    background: p.bg, color: p.fg, padding: '2px 8px', borderRadius: 10,
    fontSize: 11, fontWeight: 600
  };
};

const attendeeBadge = (response) => {
  const palette = {
    Accepted: { bg: '#D1FAE5', fg: '#065F46' },
    Declined: { bg: '#FEE2E2', fg: '#991B1B' },
    Pending:  { bg: '#FEF3C7', fg: '#92400E' }
  };
  const p = palette[response] || palette.Pending;
  return {
    background: p.bg, color: p.fg, padding: '4px 10px', borderRadius: 12,
    fontSize: 12, fontWeight: 500
  };
};
